import React, { useContext, useEffect, useMemo, useState } from "react";
import { Link, useNavigate, useParams, useSearchParams } from "react-router-dom";
import { FaAngleRight } from "react-icons/fa";

import { DashboardFooter } from "../dashboard/layout/DashboardFooter";
import { DashboardHeader } from "../dashboard/layout/DashboardHeader";
import { ZeosSpinner } from "../../components/Shared/ZeosSpinner";
import { zeosConfirmation } from "../../components/Shared/confirm-dialog/ConfirmDialog";
import { ZeosTable } from "../../components/Shared/ZeosTable/ZeosTable";
import { AuthContext } from "../../context/AuthContext";
import { ZeosApiContext } from "../../context/ZeosApiContext";
import { findClaimCompany } from "../../services/helpers/AccountClaimHelpers";
import {
  renderAiSummary,
  renderIncentiveLinkCell,
} from "../../mock-data/IncentivesTableData";
import { createLogger } from "../../services/Logger";
import type { IAiSummary } from "../../services/zeos-api/contracts/Incentive";
import {
  IProjectAiSummary,
  IProjectDetail,
  IProjectIncentiveMatch,
} from "../../services/zeos-api/contracts/Project";

import type { IColumnDefinition } from "../../components/Shared/ZeosTable/Interfaces";

const formatDate = (value?: string | null) => {
  if (!value) {
    return "";
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return date.toLocaleString();
};

const formatBytes = (bytes?: number | null) => {
  if (!bytes || Number.isNaN(bytes)) {
    return "";
  }
  const mb = bytes / (1024 * 1024);
  return `${mb.toFixed(2)} mb`;
};

const formatCurrency = (value?: number | null) => {
  if (value === null || value === undefined || Number.isNaN(value)) {
    return "";
  }
  return new Intl.NumberFormat("en-US", {
    style: "currency",
    currency: "USD",
    maximumFractionDigits: 0,
  }).format(value);
};

const formatDeadline = (endDate?: string | null) => {
  if (!endDate || endDate === "1/1/0001" || endDate === "01/01/0001") {
    return "Open";
  }
  return endDate;
};

const resolveDisplayAiSummary = (match: IProjectIncentiveMatch): IAiSummary | undefined => {
  const state = (match.aiAnalysisState || "none").toLowerCase();
  if (state === "ready" && match.aiSummary) {
    return match.aiSummary;
  }
  if (state === "pending") {
    return {
      shortText:
        "AI analysis is being prepared — refresh this page in a moment.",
    };
  }
  return {
    shortText: "AI analysis not available.",
  };
};

const parseProjectData = (raw?: string | null) => {
  if (!raw) {
    return null;
  }
  try {
    return JSON.parse(raw) as {
      version?: number;
      aiSummary?: IProjectAiSummary;
      projectData?: {
        systemSize?: string;
        yearInstalled?: string;
        expectedProduction?: string;
        buildings?: string;
        costStack?: {
          totalInstalledCost?: string;
          incentivesOrRebates?: string;
        };
        performanceIntent?: {
          primaryGoal?: string;
          expectedUsefulLifeYears?: string;
        };
        economicOutcomes?: {
          annualEnergySavings?: string;
          simplePayback?: string;
          roiIrr?: string;
        };
        optional?: {
          expectedSystemLife?: string;
          estimatedAnnualSavings?: string;
          roiIrr?: string;
        };
        uploads?: Array<{
          fileName?: string;
          sizeBytes?: number;
          contentType?: string;
          storageKey?: string;
          uploadedAt?: string;
        }>;
      };
    };
  } catch {
    return null;
  }
};

interface IIncentiveMatchRow {
  key: number;
  incentive: {
    url: string;
    urlLabel: string;
  };
  type: string;
  deadline: string;
  provider: string;
  reasons?: Array<{ criterion: string; result: boolean; reason: string }>;
  aiSummary?: IAiSummary;
  estimatedValue: string;
}

export const ProjectDetailScreen = () => {
  const api = useContext(ZeosApiContext);
  const auth = useContext(AuthContext);
  const log = useMemo(() => createLogger("ProjectDetailScreen"), []);
  const { projectId } = useParams();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [project, setProject] = useState<IProjectDetail | null>(null);
  const [activeTab, setActiveTab] = useState<"incentives" | "ai-summary" | "project-data">(
    "incentives"
  );
  const [matches, setMatches] = useState<IProjectIncentiveMatch[]>([]);
  const [matchesLoading, setMatchesLoading] = useState(false);
  const [matchesError, setMatchesError] = useState<string | null>(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [agentPrompt, setAgentPrompt] = useState("");
  const [projectCompanyId, setProjectCompanyId] = useState<number>();
  const forceAiSummary =
    searchParams.get("forceAiSummary") === "true" ||
    searchParams.get("forceAiSummary") === "1";
  const showDeleteButton =
    searchParams.get("deleteProject") === "true" ||
    searchParams.get("deleteProject") === "1";

  useEffect(() => {
    const fetchProject = async () => {
      const parsedId = Number(projectId);
      if (!parsedId || Number.isNaN(parsedId)) {
        setError("Project not found.");
        setProject(null);
        return;
      }

      setLoading(true);
      setError(null);
      try {
        const response = await api.Projects.getProjectById(parsedId, forceAiSummary);
        if (response?.message === "error" || !response?.data) {
          throw new Error(response?.message || "Unable to load project.");
        }
        setProject(response.data);
      } catch (err: any) {
        log.error("Failed to load project", err);
        setError(err?.message || "Unable to load project.");
        setProject(null);
      } finally {
        setLoading(false);
      }
    };

    fetchProject();
  }, [api.Projects, forceAiSummary, log, projectId]);

  useEffect(() => {
    setActiveTab("incentives");
  }, [projectId]);

  useEffect(() => {
    const fetchMatches = async () => {
      const parsedId = Number(projectId);
      if (!parsedId || Number.isNaN(parsedId)) {
        setMatches([]);
        return;
      }

      setMatchesLoading(true);
      setMatchesError(null);
      try {
        const response = await api.Projects.getProjectIncentiveMatches(parsedId);
        if (response?.message === "error") {
          throw new Error(response?.message || "Unable to load incentive matches.");
        }
        setMatches(response?.data ?? []);
      } catch (err: any) {
        log.error("Failed to load project incentive matches", err);
        setMatchesError(err?.message || "Unable to load incentive matches.");
        setMatches([]);
      } finally {
        setMatchesLoading(false);
      }
    };

    fetchMatches();
  }, [api.Projects, log, projectId]);


  // Projects don't carry companyId - resolve it from the user's claims so the
  // back link keeps ?company= and downstream screens stay on the right company.
  useEffect(() => {
    const propertyId = project?.propertyId;
    if (!propertyId) {
      setProjectCompanyId(undefined);
      return;
    }
    let cancelled = false;
    const resolveCompany = async () => {
      try {
        const user = await auth.zeosUserAsync();
        if (cancelled) {
          return;
        }
        const company = findClaimCompany(user?.customerClaims, undefined, propertyId);
        const ownsProperty = company?.properties?.some(
          (property) => property.propertyId === propertyId
        );
        setProjectCompanyId(ownsProperty ? company?.companyId : undefined);
      } catch (err: any) {
        // Non-fatal: the back link falls back to ?property= only, and the
        // Projects/Dashboard screens resolve the company from the property.
        log.error("Failed to resolve company for project property", err);
        if (!cancelled) {
          setProjectCompanyId(undefined);
        }
      }
    };
    resolveCompany();
    return () => {
      cancelled = true;
    };
  }, [auth, log, project?.propertyId]);

  const backParams = new URLSearchParams();
  if (project?.propertyId) {
    backParams.set("property", String(project.propertyId));
  }
  if (projectCompanyId != null) {
    backParams.set("company", String(projectCompanyId));
  }
  const backQuery = backParams.toString();
  const backUrl = `/projects${backQuery ? `?${backQuery}` : ""}`;
  const projectDataJsonRaw = project?.projectDataJson;
  const parsedProjectData = parseProjectData(projectDataJsonRaw);
  const projectData = parsedProjectData?.projectData;
  const aiSummary = parsedProjectData?.aiSummary;
  const uploads = projectData?.uploads ?? [];
  const hasProjectData = Boolean(projectData);
  const hasInvalidProjectData = Boolean(projectDataJsonRaw) && !parsedProjectData;
  const projectCategory = project?.category?.trim() ?? "";
  const showCategory =
    Boolean(projectCategory) &&
    projectCategory.toLowerCase() !== "unknown";
  const incentiveMatchRows: IIncentiveMatchRow[] = matches.map((match) => {
    const displaySummary = resolveDisplayAiSummary(match);
    const detailParams = new URLSearchParams();
    if (project?.propertyId) {
      detailParams.set("property", String(project.propertyId));
    }
    const detailQuery = detailParams.toString();
    const detailUrl = `/incentives/${match.incentiveId}${
      detailQuery ? `?${detailQuery}` : ""
    }`;
    const confidenceLabel = match.matchConfidence;
    const baseLabel = match.title || match.program || "Incentive";
    const urlLabel = confidenceLabel
      ? `${baseLabel} · ${confidenceLabel}`
      : baseLabel;

    return {
      key: match.incentiveId || match.incentiveProgramId,
      incentive: {
        url: project?.propertyId ? detailUrl : match.programUrl || detailUrl,
        urlLabel,
      },
      type: match.type || "",
      deadline: formatDeadline(match.endDate),
      provider: match.partnerName || match.provider || "",
      reasons: [],
      aiSummary: displaySummary,
      estimatedValue: formatCurrency(match.estimatedValue),
    };
  });
  const totalEstimatedValues = matches
    .map((match) => match.estimatedValue)
    .filter((value): value is number => typeof value === "number");
  const totalEstimatedValue = totalEstimatedValues.reduce(
    (sum, value) => sum + value,
    0
  );
  const totalEstimatedValueLabel =
    totalEstimatedValues.length > 0 ? formatCurrency(totalEstimatedValue) : "—";

  const hasPendingAiAnalysis = matches.some(
    (match) => (match.aiAnalysisState || "").toLowerCase() === "pending"
  );

  const incentiveMatchColumns: IColumnDefinition[] = [
    { title: "key", key: "key", visible: false, orderable: false },
    {
      title: "incentive",
      key: "incentive",
      orderable: false,
      filterable: false,
      render: (data: any, _: any, row: any) =>
        renderIncentiveLinkCell(data, Array.isArray(row) ? row[0] : row?.key),
    },
    { title: "type", key: "type", orderable: false, filterable: false },
    { title: "deadline", key: "deadline", orderable: false, filterable: false },
    { title: "provider", key: "provider", orderable: false, filterable: false },
    {
      title: "Economic Benefit",
      key: "aiSummary",
      orderable: false,
      filterable: false,
      render: (data: IAiSummary | undefined, _: any, row: any) =>
        renderAiSummary(data as IAiSummary, Array.isArray(row) ? row[0] : row?.key),
    },
    {
      title: "estimated value",
      key: "estimatedValue",
      orderable: false,
      filterable: false,
    },
  ];

  const incentiveMatchDataMapper = (rows: IIncentiveMatchRow[]) =>
    rows.map((row) => [
      row.key,
      {
        url: row.incentive.url,
        name: row.incentive.urlLabel,
        reasons: row.reasons,
        aiSummary: row.aiSummary,
      },
      row.type,
      row.deadline,
      row.provider,
      row.aiSummary,
      row.estimatedValue,
    ]);


  const handleDeleteProject = () => {
    if (!project?.projectId || deleteLoading) {
      return;
    }

    zeosConfirmation(
      "Delete this project and its uploaded files? This cannot be undone.",
      "Delete Project",
      async () => {
        setDeleteLoading(true);
        try {
          const response = await api.Projects.deleteProject(project.projectId);
          if (response?.message === "error" || response?.data !== true) {
            throw new Error(response?.message || "Failed to delete project.");
          }
          navigate(backUrl);
        } catch (err: any) {
          log.error("Failed to delete project", err);
          setError(err?.message || "Failed to delete project.");
        } finally {
          setDeleteLoading(false);
        }
      }
    );
  };

  const handleAskAgent = () => {
    log.info("Ask Incentive Agent (stub)", { prompt: agentPrompt });
  };

  return (
    <div className="flex flex-col min-h-screen bg-zeos-white-md">
      <DashboardHeader />
      <main className="flex-grow">
        <div className="w-full max-w-[1600px] mx-auto px-4 py-6 lg:px-6 space-y-6">
          <div className="space-y-2">
            <div className="flex items-start justify-between gap-4">
              <div>
                <h1 className="text-xl font-semibold text-gray-800">
                  Project
                  {showCategory ? ` > ${projectCategory}` : ""}
                  {project?.projectName ? ` > ${project.projectName}` : ""}
                </h1>
                <div className="h-0.5 w-6 bg-zeos-orange-md mt-2" />
              </div>
              <Link
                to={backUrl}
                className="inline-flex items-center cursor-pointer text-sm text-zeos-black hover:text-zeos-orange-dk transition-colors no-underline"
              >
                <span className="mr-1">←</span>
                Back to Projects
              </Link>
            </div>
            <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-3">
              <div className="text-sm text-gray-600">
                {project?.description || ""}
              </div>
              <div className="flex flex-wrap items-center gap-4 text-xs text-gray-600">
                <div>
                  <span className="font-semibold text-gray-700 mr-1">
                    Status
                  </span>
                  {project?.status || ""}
                </div>
                <div>
                  <span className="font-semibold text-gray-700 mr-1">
                    Created
                  </span>
                  {formatDate(project?.createdDate) || ""}
                </div>
                <div>
                  <span className="font-semibold text-gray-700 mr-1">
                    Last Updated
                  </span>
                  {formatDate(project?.updatedDate) || ""}
                </div>
              </div>
            </div>
          </div>

          {loading && <ZeosSpinner />}

          {error && (
            <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
              {error}
            </div>
          )}

          {!loading && !error && project && (
            <div className="space-y-6">
              {/* Ask the Incentive Agent — hidden until functionality is ready
              <div className="flex flex-col md:flex-row md:items-center md:justify-end gap-2">
                <div className="w-full md:max-w-sm">
                  <div className="text-sm font-semibold text-gray-700 mb-1">
                    Ask the Incentive Agent
                  </div>
                  <div className="flex items-center bg-white border border-gray-200 rounded px-2 py-1">
                    <input
                      type="text"
                      placeholder="Type a question"
                      value={agentPrompt}
                      onChange={(event) => setAgentPrompt(event.target.value)}
                      className="w-full bg-transparent outline-none text-sm text-gray-700"
                    />
                    <button
                      type="button"
                      onClick={handleAskAgent}
                      className="ml-2 text-zeos-orange-dk hover:text-zeos-orange-md transition-colors"
                      aria-label="Ask the Incentive Agent"
                    >
                      <FaAngleRight />
                    </button>
                  </div>
                </div>
              </div>
              */}
              <div className="flex flex-wrap gap-4 border-b border-gray-200">
                {[
                  { key: "incentives", label: "Incentives" },
                  { key: "ai-summary", label: "Smart Summary" },
                  { key: "project-data", label: "Project Data" },
                ].map((tab) => {
                  const isActive = activeTab === tab.key;
                  return (
                    <button
                      key={tab.key}
                      type="button"
                      onClick={() =>
                        setActiveTab(
                          tab.key as "incentives" | "ai-summary" | "project-data"
                        )
                      }
                      className={`px-3 py-2 text-sm font-medium border-b-2 transition-colors ${
                        isActive
                          ? "border-zeos-orange-md text-zeos-orange-md"
                          : "border-transparent text-gray-600 hover:text-zeos-orange-md"
                      }`}
                    >
                      {tab.label}
                    </button>
                  );
                })}
              </div>

              {activeTab === "incentives" && (
                <div className="bg-white shadow rounded p-6">
                  <h2 className="text-lg font-semibold mb-1">
                    Incentive Matches
                  </h2>
                  <p className="mb-3 text-sm text-slate-600">
                    Matches are ranked for this project and constrained to the
                    project category
                    {showCategory ? ` (${projectCategory})` : ""}, plus
                    cross-cutting programs.
                  </p>
                  {matchesLoading && <ZeosSpinner />}
                  {matchesError && (
                    <div className="text-sm text-red-700 bg-red-50 border border-red-200 rounded p-2">
                      {matchesError}
                    </div>
                  )}
                  {hasPendingAiAnalysis ? (
                    <div className="mb-3 rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-900">
                      AI analysis is being prepared — refresh this page in a
                      moment.
                    </div>
                  ) : null}
                  {!matchesLoading && !matchesError && (
                    <ZeosTable
                      unmappedData={incentiveMatchRows}
                      dataMapping={incentiveMatchDataMapper}
                      columnDefs={incentiveMatchColumns}
                      searchable={false}
                      pagination={true}
                      initialOrder={[]}
                      emptyMessage="No incentive matches identified for this project yet."
                      showFilters={false}
                      footerContent={
                        matches.length > 0 ? (
                          <div className="zeos-table-footer-row">
                            <div className="zeos-table-footer-summary">
                              <span className="zeos-table-footer-label">
                                Total Estimated Value
                              </span>
                              <span className="zeos-table-footer-value">
                                {totalEstimatedValueLabel}
                              </span>
                            </div>
                          </div>
                        ) : null
                      }
                    />
                  )}
                </div>
              )}

              {activeTab === "ai-summary" && (
                <div className="bg-white shadow rounded p-6 space-y-4">
                  <h2 className="text-lg font-semibold text-gray-800">
                    Smart Summary
                  </h2>
                  {!aiSummary && (
                    <p className="text-sm text-gray-600">
                      Processing recommendations. Check back shortly.
                    </p>
                  )}
                  {aiSummary?.status === "pending" && (
                    <p className="text-sm text-gray-600">
                      Processing recommendations. Check back shortly.
                    </p>
                  )}
                  {aiSummary?.status === "error" && (
                    <p className="text-sm text-red-600">
                      Unable to generate recommendations yet. Try again in a
                      moment.
                    </p>
                  )}
                  {aiSummary?.summaryText && (
                    <p className="text-sm text-gray-700">
                      {aiSummary.summaryText}
                    </p>
                  )}
                  {aiSummary?.bullets && aiSummary.bullets.length > 0 && (
                    <ul className="text-sm text-gray-700 list-disc list-inside space-y-1">
                      {aiSummary.bullets.slice(0, 4).map((bullet, index) => (
                        <li key={`project-ai-summary-${index}`}>{bullet}</li>
                      ))}
                    </ul>
                  )}
                  {aiSummary?.nextSteps && aiSummary.nextSteps.length > 0 && (
                    <div>
                      <div className="text-sm font-semibold text-gray-700 mb-2">
                        Next Steps
                      </div>
                      <ul className="text-sm text-zeos-orange-md list-disc list-inside space-y-1">
                        {aiSummary.nextSteps
                          .slice(0, 4)
                          .map((stepLabel, index) => (
                            <li key={`project-ai-step-${index}`}>
                              {stepLabel}
                            </li>
                          ))}
                      </ul>
                    </div>
                  )}
                </div>
              )}

              {activeTab === "project-data" && (
                <div className="bg-white shadow rounded p-6 space-y-6">
                  <div>
                    <h2 className="text-lg font-semibold text-gray-800 mb-2">
                      Project Data
                    </h2>
                    {!hasProjectData && (
                      <div className="text-sm text-gray-600">
                        {hasInvalidProjectData
                          ? "Project data is unavailable or invalid."
                          : "No project data available."}
                      </div>
                    )}
                  </div>
                  {hasProjectData && (
                    <div className="space-y-6 text-sm text-gray-700">
                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-4">
                          <div className="font-semibold text-gray-800">
                            System Size
                          </div>
                          <div>{projectData?.systemSize || ""}</div>
                        </div>
                        <div className="col-span-12 md:col-span-4">
                          <div className="font-semibold text-gray-800">
                            Year Installed
                          </div>
                          <div>{projectData?.yearInstalled || ""}</div>
                        </div>
                        <div className="col-span-12 md:col-span-4">
                          <div className="font-semibold text-gray-800">
                            Expected Production
                          </div>
                          <div>{projectData?.expectedProduction || ""}</div>
                        </div>
                        <div className="col-span-12">
                          <div className="font-semibold text-gray-800">
                            Buildings
                          </div>
                          <div className="whitespace-pre-wrap">
                            {projectData?.buildings || ""}
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-6 space-y-3">
                          <div className="font-semibold text-gray-800">
                            Cost Stack
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Total Installed Project Cost
                            </div>
                            <div>
                              {projectData?.costStack?.totalInstalledCost || ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Incentives / Rebates
                            </div>
                            <div>
                              {projectData?.costStack?.incentivesOrRebates || ""}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-12 md:col-span-6 space-y-3">
                          <div className="font-semibold text-gray-800">
                            Performance Intent
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Primary Goal
                            </div>
                            <div>
                              {projectData?.performanceIntent?.primaryGoal || ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Expected Useful Life (years)
                            </div>
                            <div>
                              {projectData?.performanceIntent
                                ?.expectedUsefulLifeYears || ""}
                            </div>
                          </div>
                        </div>
                      </div>

                      <div className="grid grid-cols-12 gap-4">
                        <div className="col-span-12 md:col-span-6 space-y-3">
                          <div className="font-semibold text-gray-800">
                            Economic Outcomes
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Annual Energy Savings
                            </div>
                            <div>
                              {projectData?.economicOutcomes
                                ?.annualEnergySavings || ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Simple Payback
                            </div>
                            <div>
                              {projectData?.economicOutcomes?.simplePayback || ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              ROI / IRR
                            </div>
                            <div>
                              {projectData?.economicOutcomes?.roiIrr || ""}
                            </div>
                          </div>
                        </div>
                        <div className="col-span-12 md:col-span-6 space-y-3">
                          <div className="font-semibold text-gray-800">
                            Optional
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Expected System Life
                            </div>
                            <div>
                              {projectData?.optional?.expectedSystemLife || ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">
                              Estimated Annual Savings
                            </div>
                            <div>
                              {projectData?.optional?.estimatedAnnualSavings ||
                                ""}
                            </div>
                          </div>
                          <div>
                            <div className="text-xs text-gray-500">ROI / IRR</div>
                            <div>{projectData?.optional?.roiIrr || ""}</div>
                          </div>
                        </div>
                      </div>

                      <div className="space-y-3">
                        <div className="font-semibold text-gray-800">Uploads</div>
                        {uploads.length === 0 ? (
                          <div className="text-sm text-gray-600">
                            No uploads recorded.
                          </div>
                        ) : (
                          <div className="space-y-2">
                            {uploads.map((upload, index) => (
                              <div
                                key={`${upload.fileName ?? "upload"}-${index}`}
                                className="border border-gray-100 rounded px-3 py-2 flex flex-wrap items-center justify-between gap-2"
                              >
                                <div>
                                  <div className="font-medium">
                                    {upload.fileName || "Untitled file"}
                                  </div>
                                  <div className="text-xs text-gray-500">
                                    {formatBytes(upload.sizeBytes) || ""} ·{" "}
                                    {formatDate(upload.uploadedAt) || ""}
                                  </div>
                                </div>
                              </div>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}
                </div>
              )}

              {showDeleteButton && (
                <div className="flex justify-end">
                  <button
                    type="button"
                    onClick={handleDeleteProject}
                    className="px-4 py-2 rounded border border-red-500 text-red-600 hover:bg-red-50 transition-all duration-300 disabled:opacity-60 disabled:cursor-not-allowed"
                    disabled={deleteLoading}
                  >
                    {deleteLoading ? "Deleting..." : "Delete Project"}
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>
      <DashboardFooter />
    </div>
  );
};
